// frontend/src/store/Slice/stockSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import type { RootState } from "../store";
import type { AnyAction } from "redux";

// Base URL
const BASE_URL = import.meta.env.VITE_API_URL;
const base = `${BASE_URL}/api/stock`;
const instrumentBase = `${BASE_URL}/api/instrument`;

// Helper types for API responses
type LastSeqResponse = { lastSeq: number; lastOrder: unknown | null };
type CreateResponse = { message?: string; data?: unknown };
type ListResponse = { total: number; page: number; limit: number; data: unknown[] };
type InstrumentsResponse = { data: { _id?: string; name: string; code: string }[] };
type CreateInstrumentResponse = { message?: string; data?: { _id?: string; name: string; code: string } };
type DeleteResponse = { message?: string };
type UpdateResponse = { message?: string; data?: unknown };

// Token helper (getState is a function that returns RootState)
const getToken = (getState: () => RootState): string => {
  const state: RootState = getState();
  const root = state as unknown as Record<string, unknown>;
  const auth = root["auth"] as Record<string, unknown> | undefined;
  const token = auth?.["token"] as string | undefined;
  const user = auth?.["user"] as Record<string, unknown> | undefined;
  const userToken = user?.["token"] as string | undefined;
  return token ?? userToken ?? "";
};

function getErrorPayload(err: unknown) {
  if (typeof err === "object" && err !== null) {
    const e = err as Record<string, unknown>;
    const response = e["response"] as Record<string, unknown> | undefined;
    if (response && "data" in response) return response["data"];
    if ("message" in e) return e["message"];
  }
  return String(err);
}

/* ============================================================
 ✅ Fetch last sequence
============================================================ */
export const fetchLastStockSeqAsync = createAsyncThunk<
  LastSeqResponse,
  void,
  { rejectValue: unknown; state: RootState }
>(
  "stock/fetchLastSeq",
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getToken(getState);
      const { data } = await axios.get(`${base}/last-seq`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data; // { lastSeq, lastOrder }
    } catch (err: unknown) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

/* ============================================================
 ✅ Fetch instruments
============================================================ */
export const fetchInstrumentsAsync = createAsyncThunk<
  InstrumentsResponse,
  void,
  { rejectValue: unknown; state: RootState }
>(
  "stock/instruments",
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getToken(getState);
      const { data } = await axios.get(instrumentBase, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    } catch (err: unknown) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

/* ============================================================
 ✅ Create instrument (admin only)
============================================================ */
export const createInstrumentAsync = createAsyncThunk<
  CreateInstrumentResponse,
  { name: string; code: string },
  { rejectValue: unknown; state: RootState }
>(
  "stock/createInstrument",
  async (payload, { rejectWithValue, getState }) => {
    try {
      const token = getToken(getState);
      const { data } = await axios.post(instrumentBase, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    } catch (err: unknown) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

/* ============================================================
 ✅ Create stock
============================================================ */
export const createStockAsync = createAsyncThunk<
  CreateResponse,
  unknown,
  { rejectValue: unknown; state: RootState }
>(
  "stock/create",
  async (payload, { rejectWithValue, getState }) => {
    try {
      const token = getToken(getState);
      const { data } = await axios.post(base, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data; // { message, data: createdStock }
    } catch (err: unknown) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);



/* ============================================================
 ✅ Fetch list with filters + pagination
============================================================ */
export const fetchStockListAsync = createAsyncThunk<
  ListResponse,
  { page?: number; limit?: number; search?: string; fromDate?: string; toDate?: string } | void,
  { rejectValue: unknown; state: RootState }
>(
  "stock/list",
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const token = getToken(getState);

      const queryParams: Record<string, unknown> = {};
      const p = params as { page?: number; limit?: number; search?: string; fromDate?: string; toDate?: string };
      if (p.page) queryParams.page = p.page;
      if (p.limit) queryParams.limit = p.limit;
      if (p.search) queryParams.search = p.search;
      if (p.fromDate) queryParams.fromDate = p.fromDate;
      if (p.toDate) queryParams.toDate = p.toDate;

      const { data } = await axios.get(base, {
        params: queryParams,
        headers: { Authorization: `Bearer ${token}` },
      });

      return data; // { total, page, limit, data }
    } catch (err: unknown) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

/* ============================================================
 ✅ Fetch instruments
============================================================ */
/* (No remote instrument endpoints used) */

/* ============================================================
 ✅ Delete stock
============================================================ */
export const deleteStock = createAsyncThunk<
  DeleteResponse,
  string,
  { rejectValue: unknown; state: RootState }
>(
  "stock/delete",
  async (id, { rejectWithValue, getState }) => {
    try {
      const token = getToken(getState);
      const { data } = await axios.delete(`${base}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    } catch (err: unknown) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

/* ============================================================
 ✅ Update stock
============================================================ */
export const updateStockAsync = createAsyncThunk<
  UpdateResponse,
  { id: string; payload: unknown },
  { rejectValue: unknown; state: RootState }
>(
  "stock/update",
  async ({ id, payload }, { rejectWithValue, getState }) => {
    try {
      const token = getToken(getState);
      const { data } = await axios.put(`${base}/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data; // { message, data: updatedStock }
    } catch (err: unknown) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

/* ============================================================
 ✅ Approve stock (department-wise)
============================================================ */
export const approveStockAsync = createAsyncThunk<
  { status?: string; approvals?: unknown },
  string,
  { rejectValue: unknown; state: RootState }
>(
  "stock/approve",
  async (stockId, { rejectWithValue, getState }) => {
    try {
      const token = getToken(getState);
      const { data } = await axios.put(
        `${base}/${stockId}/approve`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data; // { status, approvals }
    } catch (err: unknown) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);


/* ============================================================
 ✅ Slice
============================================================ */
interface StockState {
  items: unknown[];
  total: number;
  page: number;
  limit: number;
  lastSeq: number;
  lastOrder: unknown | null;
  instruments: { _id?: string; name: string; code: string }[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: unknown;
  message: string | null;
}

const initialState: StockState = {
  items: [] as unknown[],
  total: 0,
  page: 1,
  limit: 50,
  // instruments are handled in the frontend component (local list)
  lastSeq: 0,
  lastOrder: null,
  instruments: [],
  status: "idle",
  error: null,
  message: null,
};

const stockSlice = createSlice({
  name: "stock",
  initialState,
  reducers: {
    clearStockMessages(state) {
      state.message = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ✅ Last Seq */
      .addCase(fetchLastStockSeqAsync.fulfilled, (state, action) => {
        state.lastSeq = action.payload.lastSeq;
        state.lastOrder = action.payload.lastOrder;
      })

      /* ✅ Create */
      .addCase(createStockAsync.fulfilled, (state, action) => {
        state.message = action.payload?.message || "Created successfully";
      })


      /* ✅ Fetch list */
      .addCase(fetchStockListAsync.fulfilled, (state, action) => {
        state.items = action.payload.data;
        state.total = action.payload.total;
        state.page = action.payload.page || state.page;
        state.limit = action.payload.limit || state.limit;
      })

      /* ✅ Delete */
      .addCase(deleteStock.fulfilled, (state, action) => {
        state.message = action.payload?.message || "Deleted successfully";
      })

      /* ✅ Update */
      .addCase(updateStockAsync.fulfilled, (state, action) => {
        state.message = action.payload?.message || "Updated successfully";
        // replace in items if present
        const updated = action.payload?.data as Record<string, unknown> | undefined;
        if (updated && Array.isArray(state.items)) {
          const id = updated["_id"] as string | undefined;
          if (id) {
            const idx = state.items.findIndex((it) => {
              const itObj = it as Record<string, unknown>;
              return (itObj["_id"] as string | undefined) === id;
            });
            if (idx !== -1) state.items[idx] = updated;
          }
        }
      })


            /* ✅ Approve stock */
      .addCase(approveStockAsync.fulfilled, (state, action) => {
        state.message = "Stock approved successfully";

        const { status, approvals } = action.payload as {
          status?: string;
          approvals?: unknown;
        };

        // Update item in list
        if (Array.isArray(state.items)) {
          const idx = state.items.findIndex((it) => {
            const obj = it as Record<string, unknown>;
            return obj["_id"] === (action.meta.arg as string);
          });

          if (idx !== -1) {
            const current = state.items[idx] as Record<string, unknown>;
            state.items[idx] = {
              ...current,
              status: status ?? current["status"],
              approvals: approvals ?? current["approvals"],
            };
          }
        }
      })

      /* ✅ Fetch instruments */
      .addCase(fetchInstrumentsAsync.fulfilled, (state, action) => {
        state.instruments = action.payload?.data || [];
      })

      /* ✅ Create instrument */
      .addCase(createInstrumentAsync.fulfilled, (state, action) => {
        state.message = action.payload?.message || "Instrument created";
        const created = action.payload?.data;
        if (created) {
          state.instruments = [...state.instruments, created];
        }
      })

      /* ✅ Matchers for pending / fulfilled / rejected */
      .addMatcher(
        (action) => action.type.startsWith("stock/") && action.type.endsWith("/pending"),
        (state) => {
          state.status = "loading";
        }
      )
      .addMatcher(
        (action) => action.type.startsWith("stock/") && action.type.endsWith("/fulfilled"),
        (state) => {
          state.status = "succeeded";
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.startsWith("stock/") && action.type.endsWith("/rejected"),
        (state, action: AnyAction) => {
          state.status = "failed";
          state.error = action.payload || action.error;
        }
      );
  },
});

export const { clearStockMessages } = stockSlice.actions;
export default stockSlice.reducer;
